import { useRef, useState, useCallback } from "react";

import { UseVideoPlayerReturn } from "./types";

/**
 * Custom hook for managing video player state
 *
 * @returns Object containing videoRef, isPlaying state, and event handlers
 *
 * @example
 * ```tsx
 * const { videoRef, isPlaying, handlePlay, handlePause, handleEnded, handlePlayPause, handleSkip } = useVideoPlayer();
 * return <Video ref={videoRef} onPlay={handlePlay} onPause={handlePause} onEnded={handleEnded} />;
 * // Skip 10 seconds forward: handleSkip(10)
 * // Skip 10 seconds backward: handleSkip(-10)
 * ```
 */
const useVideoPlayer = (): UseVideoPlayerReturn => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const handlePlay = useCallback(() => setIsPlaying(true), []);
  const handlePause = useCallback(() => setIsPlaying(false), []);
  const handleEnded = useCallback(() => setIsPlaying(false), []);

  const handlePlayPause = useCallback(() => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  }, [isPlaying]);

  /**
   * Unified skip handler - accepts positive (forward) or negative (backward) seconds
   * @param seconds - Number of seconds to skip (positive = forward, negative = backward)
   */
  const handleSkip = useCallback((seconds: number) => {
    if (videoRef.current) {
      const newTime = videoRef.current.currentTime + seconds;
      videoRef.current.currentTime = Math.max(
        0,
        Math.min(videoRef.current.duration, newTime),
      );
    }
  }, []);

  const handleSkipBackward = useCallback(
    (skipSeconds: number) => handleSkip(-skipSeconds),
    [handleSkip],
  );

  const handleSkipForward = useCallback(
    (skipSeconds: number) => handleSkip(skipSeconds),
    [handleSkip],
  );

  return {
    videoRef,
    isPlaying,
    handlePlay,
    handlePause,
    handleEnded,
    handlePlayPause,
    handleSkip,
    handleSkipBackward,
    handleSkipForward,
  };
};

export { useVideoPlayer };
